import { format } from 'date-fns';
import { Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { CsatAnalyticsDto } from '@/Domain/api/Analytics/dto/CsatAnalytics.dto';
import {
  Card,
  CardHeader,
  CardDescription,
  CardTitle,
  CardContent,
} from '@/Domain/components/Shared/Card/Card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/Domain/components/Shared/Chart/chart';
import { ChartPlaceholder } from '@/Domain/components/Shared/ChartPlaceholder';

type Props = {
  data: CsatAnalyticsDto | undefined;
  isLoading: boolean;
};

export const CsatSection = ({ data, isLoading }: Props) => {
  const { t } = useTranslation('insights_page', { keyPrefix: 'ai_insights.csat' });

  const hasData = data && data.total_ratings > 0;

  const distributionData = data
    ? [1, 2, 3, 4, 5].map((star) => ({
        star: `${star}`,
        count: data.rating_distribution[star] ?? 0,
      }))
    : [];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">{t('title')}</h2>
        <p className="text-sm text-muted-foreground">{t('description')}</p>
      </div>

      {!hasData ? (
        <ChartPlaceholder
          icon={Star}
          titleTranslationKey="no_data_in_range.title"
          descriptionTranslationKey="no_data_in_range.description"
          spacing="small"
        />
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardDescription>{t('average_rating')}</CardDescription>
                <CardTitle className="text-2xl">{data.average_rating?.toFixed(1) ?? '—'}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader>
                <CardDescription>{t('total_ratings')}</CardDescription>
                <CardTitle className="text-2xl">{data.total_ratings}</CardTitle>
              </CardHeader>
            </Card>
          </div>

          {/* Daily average chart */}
          <Card>
            <CardHeader>
              <CardTitle>{t('daily_average')}</CardTitle>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{
                  average: { label: t('average_rating'), color: 'var(--chart-primary)' },
                }}
                className={`h-60 w-full ${isLoading ? 'animate-pulse' : ''}`}
              >
                <AreaChart
                  accessibilityLayer
                  data={data.ratings_by_day}
                  margin={{ left: 0, right: 0 }}
                >
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    tickFormatter={(value) => format(new Date(value), 'dd MMM')}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    domain={[0, 5]}
                    width={36}
                  />
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        indicator="dot"
                        labelFormatter={(value) => format(new Date(value), 'dd MMM yyyy')}
                      />
                    }
                  />
                  <Area
                    dataKey="average"
                    type="monotone"
                    fill="var(--color-average)"
                    fillOpacity={0.2}
                    stroke="var(--color-average)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ChartContainer>
            </CardContent>
          </Card>

          {/* Rating distribution */}
          <Card>
            <CardHeader>
              <CardTitle>{t('distribution')}</CardTitle>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{
                  count: { label: t('count'), color: 'var(--chart-primary)' },
                }}
                className="h-48 w-full"
              >
                <BarChart
                  accessibilityLayer
                  data={distributionData}
                  margin={{ left: 0, right: 0, bottom: 4 }}
                >
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="star"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    tickFormatter={(value) => `${value}★`}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    allowDecimals={false}
                    width={48}
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="count" fill="var(--color-count)" radius={[5, 5, 0, 0]} />
                </BarChart>
              </ChartContainer>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};
